import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Check, Circle, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import { useActiveClient } from '../../contexts/ActiveClientContext';
import type {
  DeliveryAudience, DeliveryControl, DeliveryNote, DeliveryProjectPanel,
} from '../../types/aai';
import { ErrorNote, Loading } from '../../components/aa';
import QuoteCard from './QuoteCard';
import { DeliveryCard } from '../owner/DeliveryCard';

/*
 * One Delivery Project, and everything the platform knows about it.
 *
 * Almost all of this is derived: the state, the owner, what happens next, the
 * related needs, the audience, and the people who can reach the work are read
 * from the request row and the client's own directory. Two things are stored
 * because nothing could derive them — the accepted quote and the partner's
 * delivery notes — and both are versioned or append-only.
 *
 * A fact with no source renders as "not recorded" rather than as a plausible
 * default. This screen is a partner's account of a client's work; a filled-in
 * blank here is a claim nobody made.
 */

const CHIP_TONE: Record<string, string> = {
  'In delivery': 'bg-canvas-inset text-ink',
  'In progress': 'bg-canvas-inset text-ink-muted',
  Pending: 'bg-[rgba(255,234,0,0.2)] text-[#8A6D00]',
  'Needs review': 'bg-[rgba(255,234,0,0.2)] text-[#8A6D00]',
  Declined: 'bg-danger-soft text-danger',
};

const chipTone = (chip: string): string => CHIP_TONE[chip] || 'bg-canvas-inset text-ink-muted';

const when = (iso: string | null): string => {
  if (!iso) return 'Not recorded';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return 'Not recorded';
  const mins = Math.round((Date.now() - at.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minutes ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hours ago`;
  return at.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

// The audience line. A group under the reporting threshold is named without a
// size, because a number that small identifies the people in it.
const audienceValue = (audience: DeliveryAudience): string => {
  if (audience.suppressed) return `${audience.scope} · size withheld`;
  if (audience.people === null) return audience.scope;
  return `${audience.people}-person ${audience.scope.toLowerCase()}`;
};

const relatedValue = (count: number, teams: number): string => {
  if (count === 0) return 'No related needs recorded';
  const needs = { true: '1 related need', false: `${count} related needs` }[String(count === 1)];
  const across = { true: '1 team', false: `${teams} teams` }[String(teams === 1)];
  return `${needs} across ${across}`;
};

const Fact: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="min-w-0">
    <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{label}</div>
    <div className="mt-1.5 text-[15px] font-bold text-ink">{value}</div>
  </div>
);

const NotRecorded: React.FC<{ reason: string }> = ({ reason }) => (
  <span className="text-[13px] font-normal text-ink-soft">{reason}</span>
);

const ControlRow: React.FC<{ control: DeliveryControl }> = ({ control }) => (
  <div className="flex items-start gap-3 rounded-card bg-canvas-inset px-4 py-3">
    {control.value && <Check size={15} strokeWidth={3} className="mt-0.5 shrink-0 text-[#3F7A2F]" />}
    {!control.value && <Circle size={14} className="mt-0.5 shrink-0 text-ink-soft" />}
    <div className="min-w-0">
      <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{control.label}</div>
      <div className="mt-0.5 text-[13px] text-ink">
        {control.value}
        {!control.value && <NotRecorded reason={control.unset} />}
      </div>
    </div>
  </div>
);

const NoteComposer: React.FC<{
  open: boolean;
  busy: boolean;
  onSubmit: (body: string) => void;
  onClose: () => void;
}> = ({ open, busy, onSubmit, onClose }) => {
  const [body, setBody] = useState('');

  if (!open) return null;

  const submit = () => {
    if (!body.trim()) {
      toast.error('Write the note first.');
      return;
    }
    onSubmit(body.trim());
    setBody('');
  };

  return (
    <div className="rounded-panel border border-line bg-white p-5 md:p-6">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">Delivery note</div>
      <h3 className="m-0 mt-1.5 text-[17px] font-bold leading-tight text-ink">Record what happened</h3>
      <p className="m-0 mt-1.5 text-[12.5px] leading-[20px] text-ink-dim">
        A note is a record. It does not change the project, notify anyone, or start any work — it is filed
        against this Delivery Project so the account of it stays with the work.
      </p>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        autoFocus
        placeholder="Walked the client owner through the renewal scope. They confirmed the pilot audience."
        className="mt-3.5 w-full resize-y rounded-card border border-line-strong bg-white px-4 py-3 text-[13.5px] leading-[21px] text-ink outline-none placeholder:text-ink-soft focus:border-ink"
      />
      <div className="mt-3.5 flex flex-wrap items-center gap-2.5">
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className="inline-flex h-11 items-center rounded-act bg-ink px-5 text-[13px] font-semibold text-white transition-colors hover:bg-black disabled:opacity-50 whitespace-nowrap"
        >
          {busy && 'Saving…'}
          {!busy && 'Save note'}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-11 items-center rounded-act border border-line-strong bg-white px-5 text-[13px] font-semibold text-ink transition-colors hover:bg-canvas-inset whitespace-nowrap"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const NoteRow: React.FC<{ note: DeliveryNote }> = ({ note }) => (
  <div className="flex flex-wrap items-start justify-between gap-3 border-t border-line-soft px-6 py-4 first:border-t-0">
    <div className="flex min-w-0 items-start gap-3.5">
      <span className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-full bg-canvas-inset font-sans text-[11px] font-bold text-ink-muted">
        {(note.author_label || 'W').charAt(0)}
      </span>
      <div className="min-w-0">
        <div className="text-[13.5px] font-semibold text-ink">{note.body}</div>
        <div className="mt-0.5 text-[12px] text-ink-soft">
          {when(note.created_at)} · {note.author_email}
        </div>
      </div>
    </div>
    <span className="shrink-0 rounded-md bg-canvas-inset px-2.5 py-1 text-[11.5px] font-semibold text-ink-muted">
      Record only
    </span>
  </div>
);

const DeliveryProjectPage: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { active, loading: clientsLoading } = useActiveClient();

  const [data, setData] = useState<DeliveryProjectPanel | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noteOpen, setNoteOpen] = useState(params.get('note') === '1');

  useEffect(() => {
    if (!active || !requestId) return;
    let live = true;
    setLoading(true);
    aaiApi.getDeliveryProject(requestId)
      .then((res) => { if (live) setData(res.data); })
      .catch(() => { if (live) setError('Could not open this Delivery Project.'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [active, requestId]);

  const reload = useCallback(async () => {
    if (!requestId) return;
    const res = await aaiApi.getDeliveryProject(requestId);
    setData(res.data);
  }, [requestId]);

  const saveNote = async (body: string) => {
    if (!requestId) return;
    setBusy(true);
    try {
      await aaiApi.addDeliveryNote(requestId, body);
      await reload();
      setNoteOpen(false);
      toast.success('Delivery note recorded.');
    } catch {
      toast.error('Could not save the note.');
    } finally {
      setBusy(false);
    }
  };

  const close = () => navigate('/partner/projects');

  if (clientsLoading || loading) {
    return (
      <div className="fixed inset-0 z-[200] grid place-items-center bg-ink/25 backdrop-blur-sm">
        <div className="rounded-panel bg-white px-8"><Loading label="Opening the Delivery Project" /></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="fixed inset-0 z-[200] grid place-items-center bg-ink/25 p-6 backdrop-blur-sm">
        <div className="w-full max-w-[480px] rounded-panel bg-white p-6">
          <ErrorNote message={error || 'That project could not be found.'} />
          <div className="mt-4 flex justify-end">
            <button type="button" onClick={close} className="h-10 rounded-act border border-line-strong bg-white px-4 text-[13px] font-semibold text-ink hover:bg-canvas-inset">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { project, related, audience, people, controls, quote, quotes, notes } = data;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-ink/30 backdrop-blur-sm">
      <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-white sm:h-[min(754px,calc(100vh-2rem))] sm:w-[min(880px,calc(100vw-2rem))] sm:rounded-panel">
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-line px-6 py-4 md:px-8 md:py-5">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">
              Projects / Delivery Project · {project.state_label}
            </span>
            <span className={`inline-flex h-6 items-center rounded-md px-2.5 text-[11.5px] font-semibold ${chipTone(project.stage_chip)}`}>
              {project.stage_chip}
            </span>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close the Delivery Project"
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-line bg-white text-ink-muted transition-colors hover:bg-canvas-inset hover:text-ink"
          >
            <X size={17} />
          </button>
        </header>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-6 md:px-8">
          <div>
            <h1 className="m-0 text-[30px] font-bold leading-tight tracking-[-0.015em] text-ink">
              {project.title}
            </h1>
            <p className="m-0 mt-2 text-[14px] leading-[23px] text-ink-muted">
              {project.detail || 'No description was recorded on this request.'}
            </p>
          </div>

          <section className="rounded-panel border border-line px-5 py-4 md:px-6">
            <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">
              Why this is a Delivery Project
            </div>
            <p className="m-0 mt-2 text-[14px] leading-[22px] text-ink">
              {project.scope_notes}
              {!project.scope_notes && (
                <NotRecorded reason="No scope note was recorded, so the reason this is a Delivery Project rather than a Quick Win has not been written down." />
              )}
            </p>
          </section>

          <section className="grid gap-6 rounded-panel border border-line px-5 py-5 sm:grid-cols-2 md:px-6">
            <Fact
              label="Owner"
              value={project.owner || <NotRecorded reason="Not yet named" />}
            />
            <Fact label="Audience" value={audienceValue(audience)} />
            <Fact label="Evidence" value={relatedValue(related.count, related.teams)} />
            <Fact label="Next" value={project.next_action} />
          </section>

          <section className="flex flex-wrap items-center justify-between gap-5 rounded-panel bg-canvas-inset px-5 py-4 md:px-6">
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              <SmallFact label="Current state" value={project.state_label} />
              <SmallFact label="Next action" value={project.next_action} />
              <SmallFact label="Owner" value={project.owner || 'Not yet named'} />
              <SmallFact label="Last update" value={when(project.decided_at || project.created_at)} />
            </div>
            <button
              type="button"
              onClick={() => setNoteOpen(true)}
              className="shrink-0 rounded-act border border-line-strong bg-white px-5 py-3 text-[13px] font-semibold text-ink transition-colors hover:bg-white/70"
            >
              Prepare update
            </button>
          </section>

          <NoteComposer open={noteOpen} busy={busy} onSubmit={saveNote} onClose={() => setNoteOpen(false)} />

          <section className="rounded-panel bg-canvas-inset px-5 py-5 md:px-6">
            <div className="flex items-start gap-4">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ink text-white">
                <Circle size={13} strokeWidth={3} />
              </span>
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">
                  Access &amp; data
                </div>
                <div className="mt-1 text-[17px] font-bold text-ink">Client assignment</div>
                <p className="m-0 mt-1.5 text-[13px] leading-[21px] text-ink-dim">
                  Time-limited client assignment. The client owner controls scope and release.
                </p>
              </div>
            </div>
            <div className="mt-4 grid divide-y divide-line rounded-card border border-line bg-white sm:grid-cols-3 sm:divide-x sm:divide-y-0">
              <TableCell label="Named on this project" value={`${people.length}`} />
              <TableCell label="Department" value={project.department || 'Whole company'} />
              <TableCell label="Source data" value="The client's own permissions" />
            </div>
            <p className="m-0 mt-3 text-[12.5px] leading-[20px] text-ink-dim">
              Client confidential · source policy remains authoritative · the client owner reviews access.
            </p>
          </section>

          <section className="rounded-panel border border-line">
            <div className="flex items-center justify-between gap-3 px-5 py-4 md:px-6">
              <span className="text-[14px] font-semibold text-ink">People &amp; access</span>
              <span className="text-[12.5px] text-ink-dim">
                {people.length} named
              </span>
            </div>
            <div className="divide-y divide-line-soft border-t border-line-soft">
              {people.map((person) => (
                <div key={person.email} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 md:px-6">
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-ink">{person.email}</div>
                    <div className="text-[12px] text-ink-dim">{person.role}</div>
                  </div>
                  <span className="shrink-0 text-[12px] text-ink-soft">{person.affiliation}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="flex items-start gap-4 rounded-panel border border-line px-5 py-4 md:px-6">
            <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-canvas-inset text-ink-muted">
              <Circle size={13} strokeWidth={3} />
            </span>
            <div className="min-w-0">
              <div className="text-[14px] font-semibold text-ink">
                {relatedValue(related.count, related.teams)} · private wording hidden
              </div>
              <p className="m-0 mt-0.5 text-[13px] leading-[21px] text-ink-dim">
                Related work is suggested for comparison and never merged automatically. What other people
                actually wrote stays theirs.
              </p>
            </div>
          </section>

          <section className="rounded-panel border border-line px-5 py-5 md:px-6">
            <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">
              Context and human control
            </div>
            <div className="mt-3 space-y-2">
              {controls.map((control) => <ControlRow key={control.label} control={control} />)}
            </div>
          </section>

          <QuoteCard
            requestId={project.id}
            quote={quote}
            quotes={quotes}
            onChanged={reload}
          />

          <DeliveryCard panel={data} onChanged={reload} />

          <section className="rounded-panel border border-line">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 md:px-6">
              <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">
                Recent Delivery Project activity
              </span>
              {/*
              <button
                type="button"
                onClick={() => navigate('/partner/context?tab=activity')}
                className="text-[13px] font-semibold text-ink transition-colors hover:text-ink-muted"
              >
                Full history →
              </button>
              */}
            </div>
            {notes.length === 0 && (
              <p className="m-0 border-t border-line-soft px-5 py-6 text-center text-[13px] text-ink-dim md:px-6">
                Nothing recorded against this project yet. A delivery note appears here the moment you save one.
              </p>
            )}
            {notes.length > 0 && (
              <div className="border-t border-line-soft">
                {notes.map((note) => <NoteRow key={note.id} note={note} />)}
              </div>
            )}
          </section>
        </div>

        <footer className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-line px-6 py-3.5 md:px-8">
          <span className="text-[12.5px] text-ink-dim">
            You prepare and record. The client owner controls scope and release.
          </span>
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={() => setNoteOpen(true)}
              className="h-11 rounded-act border border-line-strong bg-white px-5 text-[13px] font-semibold text-ink transition-colors hover:bg-canvas-inset"
            >
              Add delivery note
            </button>
            <button
              type="button"
              onClick={close}
              className="h-11 rounded-act border border-line-strong bg-white px-5 text-[13px] font-semibold text-ink transition-colors hover:bg-canvas-inset"
            >
              Close
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

const SmallFact: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="min-w-0">
    <div className="text-[12px] text-ink-dim">{label}</div>
    <div className="mt-0.5 text-[13px] font-semibold text-ink">{value}</div>
  </div>
);

const TableCell: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="px-4 py-3">
    <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{label}</div>
    <div className="mt-1 text-[13px] font-semibold text-ink">{value}</div>
  </div>
);

export default DeliveryProjectPage;
